import { CollisionPriority, CollisionType, type CollisionDetector } from '@dnd-kit/abstract';
import { useSortable } from '@dnd-kit/react/sortable';

import type { UseDndRowOptions, UseDndRowResult } from './types.ts';

// Pure pointer-in-row-line collision detector — NO shape-intersection fallback. The droppable shape is just
// the row line (see `targetRef`), so while the pointer is over an expanded row's tall body it matches nothing
// and there's no drop target. That's deliberate: with live reordering on, a short row dragged past a tall
// expanded row would otherwise keep colliding with that row after the swap and oscillate. Restricting hits to
// the row line means a swap only happens as the pointer crosses another row's line.
//
// Only the pointer's vertical position is tested: rows are full-width and stacked, so the row under the
// pointer is determined by Y alone. (Testing X too would drop the target whenever the pointer drifted past
// the table's horizontal edges mid-drag, stalling the reorder.) The collision observer only feeds this
// same-group droppables (via accept/type), so it stays scoped to the dragged row's group.
const pointerOverRowLine: CollisionDetector = ({ droppable, dragOperation }) => {
  const { shape } = droppable;
  const pointer = dragOperation.position.current;
  if (!shape || !pointer) return null;
  const { top, bottom } = shape.boundingRectangle;
  if (pointer.y < top || pointer.y > bottom) return null;
  const distance = Math.abs(shape.center.y - pointer.y);
  return {
    id: droppable.id,
    value: 1 / (distance + 1),
    type: CollisionType.PointerIntersection,
    priority: CollisionPriority.High,
  };
};

/**
 * Makes a single table row sortable. Decoupled from how the row renders, so it composes with the rich
 * `Table.Row` (expandable content, sub-rows, selection, actions) instead of forcing the config-driven `DndRow`.
 *
 * When `group` is provided, the row is scoped to that group via `type` + `accept` so a draggable only drops
 * onto rows of the same group — multiple independent sortable lists live inside a single `DndProvider`, each
 * reordering on its own. @dnd-kit reorders the rows live during the drag (and reverts on cancel); the final
 * order is committed in `useDnd`'s drag-end handler.
 *
 * `targetRef` is the droppable target: attach it to the row's main line so the drop hit area is the row line
 * only — not an expanded row's tall body — which keeps reordering steady when dragging past expanded rows.
 * `isDragging` stays true through the drop animation so the source row can stay hidden while the overlay settles.
 */
export function useDndRow({ id, index, group }: UseDndRowOptions): UseDndRowResult {
  const { ref, handleRef, targetRef, isDragging, isDropping } = useSortable(
    group == null
      ? { id, index, collisionDetector: pointerOverRowLine }
      : { id, index, group, type: group, accept: group, collisionDetector: pointerOverRowLine },
  );

  return { ref, dragHandleRef: handleRef, targetRef, isDragging: isDragging || isDropping };
}
